/**
 * Helper that produces a `ServiceError`-shaped object suitable for
 * passing to `sendUnaryData` callbacks. Lifted from provisioning's
 * `server/grpc/utils/grpcErrors.ts`.
 */
import {
  Metadata,
  type ServiceError,
  status as grpcStatus,
  type status,
} from "@grpc/grpc-js";
import { JtiReplayed } from "../types/jti-replayed";
import { S2SReplayStoreUnavailable, S2SVerifyError } from "../verify/errors";

export function grpcError(
  code: status,
  message: string,
  details?: string,
): ServiceError {
  const err = new Error(message) as ServiceError;
  err.code = code;
  err.details = details ?? message;
  err.metadata = new Metadata();
  return err;
}

/**
 * Map a verify-time error to the gRPC status a caller should see.
 *
 * ⚠️ WHY THIS EXISTS. Go has `grpcStatusFor` (go/grpc-auth/server.go) and Python
 * has `_grpc_code_for` (python/.../server.py); TypeScript had NEITHER, and every
 * TS server entry point flattened EVERY verify throw to `UNAUTHENTICATED` at its
 * own local `catch`. So the replay-store-outage fix — the whole point of which
 * is that an infra outage must NOT be reported as a credential failure — landed
 * in two ports of three, and on the real TS gRPC path a Redis blip still told
 * the caller "your token is bad, do not retry".
 *
 * That is a hollow twin of exactly the kind this library's parity programme
 * exists to prevent, and no test caught it: `grpc-auth`'s fence is quarantined,
 * and the 503 that "proved" the behaviour came from the synthetic consumer's own
 * hand-written HTTP mapper, never from the library's wire boundary.
 *
 * ⚠️ SCOPE — narrowed after the claim audit called the original wording an
 * over-claim, and it was right. This is NOT a general analogue of Go's
 * `grpcStatusFor` / Python's `_grpc_code_for`. Those map EVERY server-side auth
 * error, including `ScopeMissingError` / `UnlistedMethodDeniedError` ->
 * PermissionDenied and (Python) `UserContextInvalidError` -> InvalidArgument.
 * This maps VERIFY-time errors only, which is all that reaches it: TypeScript has
 * no such error classes — its scope, unlisted-method and user-context paths call
 * `grpcError(status.PERMISSION_DENIED | INVALID_ARGUMENT, ...)` DIRECTLY and
 * return, so they never route through here and cannot be collapsed by it.
 *
 * The `ServiceError` passthrough below keeps that true structurally rather than
 * by inspection: if any pre-coded error ever IS routed here, it keeps its own
 * status instead of being flattened to UNAUTHENTICATED.
 *
 * ⚠️ AND IT DOES NOT COVER THE OTHER DEPENDENCY ON THIS PATH. `verifyS2SToken`
 * also calls the JWKS resolver (S3); a non-JOSE throw from it is wrapped as
 * `S2STokenInvalid` at s2s-verify.ts and therefore still answers UNAUTHENTICATED
 * — the same defect class, same function, same wire boundary. Tracked
 * separately; do not read this mapper as "infra outages are handled".
 *
 * ORDER IS LOAD-BEARING, and it is the same order both other ports document:
 * `S2SReplayStoreUnavailable` must be tested BEFORE the Unauthenticated family.
 * It is a subclass of `S2SVerifyError`, so a generic arm placed first swallows
 * it — which is how it was being reported as UNAUTHENTICATED in the first place.
 * `UNAVAILABLE` is the one status whose documented client semantics are "retry".
 */
export function grpcStatusForVerifyError(err: unknown): status {
  // A pre-coded ServiceError (from `grpcError`) already carries the right
  // status — PERMISSION_DENIED for a scope denial, INVALID_ARGUMENT for a
  // malformed envelope. Keep it rather than flattening, so this mapper can
  // never collapse a distinction the other two ports make.
  const coded = (err as { code?: unknown } | null)?.code;
  if (typeof coded === "number") return coded as status;
  // The store being down says NOTHING about the caller's credentials.
  if (err instanceof S2SReplayStoreUnavailable) return grpcStatus.UNAVAILABLE;
  // A replayed jti IS a rejection of this presentation of the token.
  if (err instanceof JtiReplayed) return grpcStatus.UNAUTHENTICATED;
  // Everything else in the verify family is a credential problem.
  if (err instanceof S2SVerifyError) return grpcStatus.UNAUTHENTICATED;
  // Unknown throws keep the pre-existing default rather than silently widening.
  return grpcStatus.UNAUTHENTICATED;
}
